<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import PersonalNoteSections from './PersonalNoteSections.svelte';
  import PersonalNoteHistory from './PersonalNoteHistory.svelte';
  import { createPersonalNotesApi, createNoteAutosaver, classDescription } from './personalNotes.js';
  import { getApiBase } from './apiBase.js';
  import { registerPersonalNotesNavigationGuard } from './personalNotesNavigation.js';

  export let anchorEventId;
  const dispatch = createEventDispatcher();
  const api = createPersonalNotesApi({ apiBase: getApiBase(), token: localStorage.getItem('jwtToken') });
  let panel, closeButton, selectedEventId = anchorEventId;
  let note = null, draft = null, saver, loading = true, loadError = '', actionError = '';
  let historyRows = [], historyCursor = null, historyLoading = false, historyError = '';
  let destroyed = false, loadSequence = 0, busy = false;

  async function loadSelected(eventId) {
    const sequence = ++loadSequence;
    loading = true;
    loadError = '';
    actionError = '';
    saver?.dispose();
    saver = null;
    draft = null;
    selectedEventId = eventId;
    try {
      const result = await api.read(eventId);
      if (destroyed || sequence !== loadSequence) return;
      note = result;
      let emittedRevision = result.revision;
      saver = createNoteAutosaver({ initial: result,
        writeNote: payload => api.save(eventId, payload),
        writeTemplate: payload => api.template(eventId, payload),
        onChange: state => {
          draft = state;
          if (state.revision !== emittedRevision) {
            emittedRevision = state.revision;
            historyRows = historyRows.map(row => row.event_id === eventId ? { ...row, has_note: state.exists } : row);
            dispatch('notechange', { eventId, exists: state.exists });
          }
        },
      });
      draft = { ...saver.state };
    } catch (error) {
      if (!destroyed && sequence === loadSequence) loadError = error.message;
    } finally {
      if (!destroyed && sequence === loadSequence) loading = false;
    }
  }

  async function loadHistory(reset = false) {
    if (historyLoading) return;
    historyLoading = true;
    historyError = '';
    try {
      const result = await api.history(anchorEventId, reset ? null : historyCursor);
      if (destroyed) return;
      const combined = reset ? result.results : [...historyRows, ...result.results];
      historyRows = [...new Map(combined.map(row => [row.event_id, row])).values()];
      historyCursor = result.next_cursor;
    } catch (error) { if (!destroyed) historyError = error.message; }
    finally { if (!destroyed) historyLoading = false; }
  }

  export async function flush() { return saver ? saver.flush() : true; }

  export async function requestClose() {
    if (busy) return false;
    busy = true;
    try {
      if (!await flush()) return false;
      dispatch('close');
      return true;
    } finally { busy = false; }
  }

  async function select(eventId) {
    if (busy || loading || selectedEventId === eventId) return;
    busy = true;
    try { if (await flush()) await loadSelected(eventId); }
    finally { busy = false; }
  }

  async function discard() {
    if (busy || draft?.saving || !window.confirm('Atmesti neišsaugotus pakeitimus ir įkelti išsaugotą versiją?')) return;
    await loadSelected(selectedEventId);
  }

  async function removeNote() {
    if (busy || !window.confirm('Ištrinti šios pamokos asmeninį užrašą su visais skyriais? Pasikartojantys skyriai liks.')) return;
    busy = true;
    actionError = '';
    try {
      if (!await flush()) return;
      await api.remove(selectedEventId, saver.state.revision);
      dispatch('notechange', { eventId: selectedEventId, exists: false });
      await loadSelected(selectedEventId);
      await loadHistory(true);
    } catch (error) { actionError = error.message; }
    finally { busy = false; }
  }

  function beforeUnload(event) {
    if (!draft?.dirty && !draft?.saving) return;
    event.preventDefault();
    event.returnValue = '';
  }

  function keydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); requestClose(); }
    if (event.key !== 'Tab' || !panel) return;
    const controls = [...panel.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea, a[href], summary')].filter(node => node.getClientRects().length);
    if (!controls.length) return;
    if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls[controls.length - 1].focus(); }
    else if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) { event.preventDefault(); controls[0].focus(); }
  }

  onMount(() => {
    closeButton.focus();
    loadSelected(anchorEventId);
    loadHistory(true);
    return registerPersonalNotesNavigationGuard({ isDirty: () => draft?.dirty || draft?.saving, flush });
  });
  onDestroy(() => { destroyed = true; loadSequence++; saver?.dispose(); });
</script>

<svelte:window on:beforeunload={beforeUnload} />

<aside class="personal-notes-panel" role="dialog" aria-modal="true" aria-labelledby="personal-notes-heading" bind:this={panel} on:keydown={keydown}>
  <header>
    <h2 id="personal-notes-heading">Asmeniniai pamokos užrašai</h2>
    <button type="button" class="close" aria-label="Uždaryti užrašus" on:click={requestClose} disabled={busy} bind:this={closeButton}>×</button>
  </header>
  <p class="privacy">Matomi tik jums ir administratoriams.</p>
  <div class="panel-scroll">
    {#if loading}<p>Įkeliami užrašai...</p>{/if}
    {#if loadError}
      <p role="alert">{loadError}</p>
      <button type="button" on:click={() => loadSelected(selectedEventId)}>Bandyti dar kartą</button>
    {/if}
    {#if note && draft && !loading}
      <section class="selected-class" aria-label="Pasirinkta pamoka">
        <h3>{note.class_info.title}</h3><p>{classDescription(note.class_info)}</p>
        <p>{note.class_info.subject || 'Neatpažintas dalykas'}</p>
      </section>
      {#if !note.matching_available}
        <p class="hint">Užrašai skirti tik šiai pamokai. Istorijai ir pasikartojantiems skyriams reikia atpažinto dalyko ir mokinių.</p>
      {/if}
      <p class="save-status" role="status" aria-live="polite">{draft.saving ? 'Saugoma...' : draft.error ? 'Nepavyko išsaugoti' : draft.dirty ? 'Neišsaugoti pakeitimai' : draft.exists ? 'Išsaugota' : 'Nauji užrašai'}</p>
      {#if draft.error}
        <div class="save-error" role="alert">
          <p>{draft.conflict ? 'Užrašai pakeisti kitame lange. Nukopijuokite savo juodraštį ir įkelkite naujausią versiją.' : draft.error}</p>
          {#if !draft.conflict}<button type="button" disabled={draft.saving} on:click={flush}>Bandyti dar kartą</button>{/if}
          <details><summary>Juodraščio kopija</summary>
            <textarea readonly aria-label="Juodraščio kopija" rows="8" value={draft.sections.map(row => `${row.title}\n${row.text}`).join('\n\n')}></textarea>
          </details>
        </div>
      {/if}
      <PersonalNoteSections sections={draft.sections} headings={draft.headings} matchingAvailable={note.matching_available}
        on:change={event => saver.edit(event.detail.sections, event.detail.headings)} />
      <div class="note-actions">
        {#if draft.exists}<button type="button" class="delete-note-danger" disabled={busy || draft.saving} on:click={removeNote}>Ištrinti užrašą</button>{/if}
        {#if draft.dirty}<button type="button" disabled={busy || draft.saving} on:click={discard}>{draft.conflict ? 'Įkelti naujausią versiją' : 'Atmesti juodraštį'}</button>{/if}
      </div>
    {/if}
    {#if actionError}<p role="alert">{actionError}</p>{/if}
    <PersonalNoteHistory rows={historyRows} selected={selectedEventId} nextCursor={historyCursor} loading={historyLoading} error={historyError}
      on:select={event => select(event.detail)} on:more={() => loadHistory()} />
  </div>
</aside>

<style>
  .personal-notes-panel { position: fixed; z-index: 1100; right: 0; top: 0; bottom: 0; width: min(560px, 100vw); box-sizing: border-box; display: flex; flex-direction: column; color: #24251f; background: #faf9f3; box-shadow: -5px 0 24px #0002; border-left: 1px solid #cdcebf; font-family: inherit; }
  header { display: flex; gap: 12px; align-items: center; justify-content: space-between; padding: 20px 24px 0; }
  h2 { font-size: 21px; margin: 0; }
  h3 { overflow-wrap: anywhere; }
  .privacy { margin: 8px 24px 16px; font-size: 13px; }
  .panel-scroll { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 0 24px 28px; }
  .selected-class { border-bottom: 1px solid #dedbcf; padding-bottom: 12px; }
  .selected-class p { margin: 6px 0; font-size: 14px; }
  .save-status { font-size: 13px; color: #4b5a37; }
  .save-error { background: #f9e7df; border: 1px solid #b05b40; padding: 12px; border-radius: 5px; }
  .save-error p { margin-top: 0; }
  .save-error textarea { box-sizing: border-box; width: 100%; }
  .hint { font-size: 13px; }
  .note-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 18px; }
  button { padding: 9px 12px; min-height: 40px; border: 1px solid #989b8a; background: #f4f3ea; color: #24251f; border-radius: 5px; cursor: pointer; font: inherit; font-size: 14px; }
  button:disabled { opacity: .5; cursor: default; }
  .delete-note-danger { background: #fbecea; border-color: #d6a09b; color: #9b3530; }
  .delete-note-danger:hover:not(:disabled), .delete-note-danger:focus-visible { background: #f5d9d5; border-color: #bd6c65; }
  .close { font-size: 26px; min-width: 44px; }
  @media (max-width: 600px) { header { padding: 16px 16px 0; } .privacy { margin-left: 16px; } .panel-scroll { padding: 0 16px 24px; } }
</style>
